import React, { useCallback, useMemo } from "react";
import { Entity, EntityWithUser } from "@common/types/entity";
import { FinancialAccount } from "@common/types/financialAccount";
import { FinancialConnection } from "@common/types/financialConnection";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { ExcludeEmptyMockEntitiesMode } from "@hooks/useEntitiesContexts";
import { useEntities } from "@hooks/useEntities";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { EntityDisplay } from "@components/entity/EntityDisplay";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { WorkspaceAction } from "@common/userWorkspaceRoles";
import { useActiveWorkspaceRole } from "@hooks/permissions/useActiveWorkspaceRole";
import { useCanPerformWorkspaceAction } from "@hooks/permissions/useCanPerformWorkspaceAction";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useUser } from "@hooks/useUser";
import { isConnectionOwner } from "@hooks/useConnectionOwnership";
import { useChangeFinancialAccountEntityMutation } from "@mutations/financialAccount";
import { ENTITY_CHANGE_RESTRICTION_TOOLTIP } from "./constants";
import { useEntityChangeWithWarnings } from "./useEntityChangeWithWarnings";

interface Props {
    account: FinancialAccount;
    selectedEntity?: Entity;
    disabled?: boolean;
    onUpdated?: () => void;
    allowMockEntity?: boolean;
    isBusiness?: boolean;
    connection?: FinancialConnection | IntegrationConnection;
}

export const UpdateFinancialAccountEntity: React.FC<Props> = ({
    account,
    selectedEntity,
    disabled,
    onUpdated,
    allowMockEntity = false,
    isBusiness,
    connection,
}) => {
    const businessEntities = useEntities({
        onlyBusiness: isBusiness === true ? true : undefined,
        onlyPersonal: isBusiness === false ? true : undefined,
        excludeEmptyMockEntities: allowMockEntity
            ? ExcludeEmptyMockEntitiesMode.NEVER
            : ExcludeEmptyMockEntitiesMode.REQUIRE_ACCOUNTS,
    });

    const displayedEntities = useMemo(() => {
        const selectedBusinessEntity = businessEntities.some(
            (e) => e.id === selectedEntity?.id,
        );

        if (selectedBusinessEntity) {
            return businessEntities;
        } else {
            return businessEntities.concat(
                selectedEntity ? [selectedEntity as EntityWithUser] : [],
            );
        }
    }, [selectedEntity, businessEntities]);

    const entityItems = useMemo<Array<PickerItemData<string>>>(
        () =>
            displayedEntities.map((entity) => ({
                value: entity.id.toString(),
                label: entity.name ?? "",
                leading: <EntityAvatar entity={entity} size="xs" aria-hidden />,
            })),
        [displayedEntities],
    );

    const changeFinancialAccountEntity =
        useChangeFinancialAccountEntityMutation(account);

    const { initiateEntityChange, confirmationModal, isVerifyingEntityChange } =
        useEntityChangeWithWarnings({
            account,
            mutation: changeFinancialAccountEntity,
            onSuccess: onUpdated,
        });

    const onValueChange = useCallback(
        async (value: string | null) => {
            const entity = displayedEntities.find(
                (e) => e.id.toString() === value,
            );
            if (!entity || entity.id === selectedEntity?.id) {
                return;
            }

            await initiateEntityChange(entity);
        },
        [displayedEntities, selectedEntity?.id, initiateEntityChange],
    );

    const triggerValue = useMemo(
        () =>
            selectedEntity ? (
                <EntityDisplay entity={selectedEntity} />
            ) : undefined,
        [selectedEntity],
    );

    const role = useActiveWorkspaceRole();
    const canEditEntitySettings = useCanPerformWorkspaceAction(
        WorkspaceAction.ENTITY_SETTINGS_EDIT,
    );
    // An unassigned account is being assigned for the first time by the user
    // who connected it; the endpoint allows connection owners, so the gate
    // only applies once an entity is set (PRO-11508). View-only keeps a live
    // dropdown and gets told why on click; every other role the change-entity
    // endpoint refuses keeps a disabled dropdown and a tooltip. Gated on a
    // resolved role because `useCanPerformWorkspaceAction` fails closed while
    // the workspace loads, which would otherwise disable the dropdown for an
    // admin mid-load.
    const { isViewOnly } = useViewOnlyRefusal();
    const user = useUser();
    const isOwner = connection ? isConnectionOwner(connection, user.id) : false;
    const isUnassigned = account.entityId === null;
    const isRestricted =
        !isUnassigned &&
        role !== null &&
        !canEditEntitySettings &&
        !isViewOnly &&
        !isOwner;
    const isDisabled = disabled ?? selectedEntity?.isPersonal;

    return (
        <>
            <Tooltip
                content={isRestricted ? ENTITY_CHANGE_RESTRICTION_TOOLTIP : ""}
            >
                <Picker.Select
                    items={entityItems}
                    value={selectedEntity?.id.toString() ?? null}
                    onValueChange={onValueChange}
                    disabled={
                        isDisabled || isVerifyingEntityChange || isRestricted
                    }
                    placeholder="Select entity"
                    renderValue={() => triggerValue}
                />
            </Tooltip>

            {confirmationModal}
        </>
    );
};
